'use client'

import { Button, Popover, PopoverDropdown, PopoverTarget } from '@mantine/core'
import { DatePicker, DatesProvider } from '@mantine/dates'
import dayjs from 'dayjs'
import { useRouter } from 'next/navigation'

import { useState, useTransition } from 'react'
import { Icon } from '~/components/icons/icon'
import { useQS } from '~/hooks/use_qs'
import { cn } from '~/utils/css'
import { today, tomorrow } from '~/utils/date'

type DateSelectorProps = {
  numberOfColumns?: 1 | 2
  showIcon?: boolean

  /**
   * TODO: This seems to be a hack we need a right way to handle this
   */
  isRefresh?: boolean
}
export function DateSelector({
  showIcon = true,
  isRefresh = false,
  numberOfColumns = 2
}: DateSelectorProps) {
  const [qs, setQS] = useQS()
  const router = useRouter()
  const [, startTransition] = useTransition()

  const [isOpen, setIsOpen] = useState(false)
  const [value, setValue] = useState<[Date | null, Date | null]>([
    dayjs(qs.checkIn).toDate(),
    dayjs(qs.checkOut).toDate()
  ])

  const [from, to] = value
  const isDefaultDate =
    dayjs(today).isSame(from, 'date') && dayjs(tomorrow).isSame(to, 'date')

  return (
    <Popover
      position="bottom"
      opened={isOpen}
      middlewares={{ shift: true, inline: true }}
      onChange={() => setIsOpen((value) => !value)}
      classNames={{ dropdown: cn('z-auto') }}
    >
      <PopoverTarget>
        <Button
          size="sm"
          color="gray"
          w="full"
          variant="subtle"
          className="w-full min-w-48 max-w-full flex-1 text-sm font-normal text-slate-800"
          leftSection={showIcon ? <Icon name="calendar" /> : null}
          rightSection={<Icon name="chevron-down" />}
          classNames={{
            root: cn(
              'hover:bg-slate-100 justify-start w-full overflow-visible'
            ),
            inner: cn('justify-start'),
            label: 'font-normal text-sm overflow-visible'
          }}
          onClick={() => setIsOpen((value) => !value)}
        >
          {`${dayjs(from || today).format('MMM D, YYYY')}  - ${to ? dayjs(to).format('MMM D, YYYY') : 'Select date'}`}
        </Button>
      </PopoverTarget>
      <PopoverDropdown styles={{ dropdown: { border: 'none', zIndex: 201 } }}>
        <div className="flex flex-col gap-10">
          <DatesProvider settings={{ consistentWeeks: true }}>
            <DatePicker
              value={value}
              defaultValue={value}
              defaultDate={from!}
              size="sm"
              type="range"
              numberOfColumns={numberOfColumns}
              minDate={new Date()}
              classNames={{ levelsGroup: 'flex-col md:flex-row' }}
              onChange={setValue}
            />
          </DatesProvider>

          <div className="self-end">
            <div className="flex gap-3">
              <Button
                size="xs"
                color="gray"
                variant="subtle"
                onClick={() => {
                  const { checkIn, checkOut } = qs
                  if (!checkIn && !checkOut) {
                    setValue([today, tomorrow])
                  }

                  if (qs.checkIn && qs.checkOut) {
                    setValue([
                      dayjs(qs.checkIn).toDate(),
                      dayjs(qs.checkOut).toDate()
                    ])
                  }

                  setIsOpen(false)
                }}
              >
                Cancel
              </Button>
              <Button
                size="xs"
                disabled={isDefaultDate || !from || !to}
                onClick={() => {
                  setQS({
                    checkIn: dayjs(from).format('YYYY-MM-DD'),
                    checkOut: dayjs(to).format('YYYY-MM-DD')
                  })

                  setIsOpen(false)
                  // INFO: We added this to refresh data on the property page
                  if (isRefresh) {
                    startTransition(() => {
                      router.refresh()
                    })
                  }
                }}
              >
                Update
              </Button>
            </div>
          </div>
        </div>
      </PopoverDropdown>
    </Popover>
  )
}
